import { useEffect, useState } from 'react';
import { snapdom } from '@zumer/snapdom';
import ConfiguratorMetaobject from '../Interfaces/ConfiguratorMetaobject';
import ShapeInterface from '../Interfaces/ShapeInterface';
import FormBuilder from './FormBuilder';
import FormStep from './FormStep';
import Step2 from './Steps/Step2';
import Step3 from './Steps/Step3';
import Step4 from './Steps/Step4';
import Step5 from './Steps/Step5';
import Step6 from './Steps/Step6';
import * as Yup from 'yup';
import { clearConfiguratorJourneySource, getConfiguratorJourneySource, getSessionStorageKey, initializeConfiguratorJourneySource, getCurrentStep, getFormFromSessionStorage, getParamByName, getStoredForm, sectionSettings, selectedConfiguratorModel, setParamByName } from '../Helpers/WindowHelpers';
import ConfiguratorValue from '../Interfaces/ConfiguratorValue';
import { getShape, updateModelObjectValues } from '../Helpers/FormikUpdateFunctions';


interface CoverConfiguratorInterface {
    metaobject: ConfiguratorMetaobject;
    shapes: ShapeInterface[];
}

const setValueAtPath = (values: any, path: string, value: any) => {
    const parts = path.split('.');
    const finalKey = parts.pop();
    if (!finalKey) return;

    const target = parts.reduce((current, part) => {
        current[part] = current[part] ?? {};
        return current[part];
    }, values);

    target[finalKey] = value;
}

const applySelectedModelInitialValues = (values: any) => {
    if (!selectedConfiguratorModel?._brand || !selectedConfiguratorModel?._model) return values;

    const next = JSON.parse(JSON.stringify(values));
    updateModelObjectValues(selectedConfiguratorModel, {
        values: next,
        setFieldValue: (path: string, value: any): void => setValueAtPath(next, path, value),
        setFieldTouched: (): void => {}
    } as any);

    return next;
}

const CoverConfigurator = ({ shapes, metaobject }: CoverConfiguratorInterface) => {
    const [coverVariant, setCoverVariant] = useState<string>();

    const sessionStorageForm = getFormFromSessionStorage();
    const defaultInitialValues = {
        page: window.location.pathname,
        brand: '',
        model: '',
        shape: '',
        customFeatures: false,
        attestation: false,
        customShape: false,
        customStrapLocation: false,
        cad: false,
        oversize_fee: {
            value: '',
            variantId: '',
            price: '',
            productId: ''
        },
        dimension_a: {
            value: '',
            variantId: '',
            price: '',
            disableContinuation: true,
        },
        dimension_b: {
            value: '',
            variantId: '',
            price: '',
            disableContinuation: false,
        },
        dimension_c: {
            value: '',
            variantId: '',
            price: '',
            disableContinuation: false,
        },
        dimension_d: {
            value: '',
            variantId: '',
            price: '',
            disableContinuation: false,
        },
        dimensionsValid: false,
        panels: '2',
        fold_direction: '',
        strap_location: '',
        custom_tiedown_distance: '',
        skirt_length: {
            value: '',
            variantId: '',
            price: '',
            productId: ''
        },
        strap_length: {
            value: '',
            variantId: '',
            price: '',
            productId: ''
        },
        strap_option: {
            value: metaobject.strap_options?.length >= 1 ? metaobject.strap_options[0].title : '',
            variantId: metaobject.strap_options?.length >= 1 ? metaobject.strap_options[0].id : '',
            price: metaobject.strap_options?.length >= 1 ? metaobject.strap_options[0].price : '',
            productId: metaobject.strap_options?.length >= 1 ? metaobject.strap_options[0].product : '',
            step: 3
        },
        handle_option: {
            value: metaobject.handle_options?.length >= 1 ? metaobject.handle_options[0].title : '',
            variantId: metaobject.handle_options?.length >= 1 ? metaobject.handle_options[0].id : '',
            price: metaobject.handle_options?.length >= 1 ? metaobject.handle_options[0].price : '',
            productId: metaobject.handle_options?.length >= 1 ? metaobject.handle_options[0].product : '',
            step: 3
        },
        color: {
            value: '',
            variantId: '',
            price: '',
            productId: ''
        },
        //@ts-ignore
        upgrade_filter: [],
        //@ts-ignore
        variants: []
    };
    const selectedModelInitialValues = applySelectedModelInitialValues(defaultInitialValues);
    const cachedInitialValues = sessionStorageForm ? { ...selectedModelInitialValues, ...sessionStorageForm } : selectedModelInitialValues;
    const synthesizedInitialValues = getParamByName('key') ? cachedInitialValues : applySelectedModelInitialValues(cachedInitialValues);

    const [initialValues, setInitialValues] = useState<any>(synthesizedInitialValues);
    const customShapeRule = (shape: any, schema: Yup.StringSchema, error: string) => {
        if (!shape) return schema.required(error);
        var currentShape = getShape(shape);
        return currentShape?.custom == "true" || metaobject.rolling_covers
            ? schema.notRequired().nullable()
            : schema.required(error)
    }
    const stepValidations = [
        Yup.object({
            shape: Yup.string().required(sectionSettings.error_shape_required),
            dimensionsValid: Yup.boolean().required('required').oneOf([true], sectionSettings.error_dimensions_invalid),
            customFeatures: Yup.boolean(),
            attestation: Yup.boolean().when('shape', (shape, schema) =>
                //@ts-ignore
                shape != 'custom'
                    ? schema.oneOf([true], sectionSettings.error_dimensions_attestation_required)
                    : schema
            ),
            dimension_a: Yup.object({
                value: Yup.string().nullable(),
            }),
            dimension_b: Yup.object({
                value: Yup.string().nullable(),
            }),
            dimension_c: Yup.object({
                value: Yup.string().nullable(),
            }),
            dimension_d: Yup.object({
                value: Yup.string().nullable(),
                // disableContinuation: Yup.boolean().when('shape', (shape, schema) => {
                //     //@ts-ignore
                //     const currentShape = getShape(shape);
                //     return currentShape?.dimensions?.find((dim) => dim.dimension == 'd')
                //         ? schema.oneOf([true], sectionSettings.error_dimensions_attestation_required)
                //         : schema
                // })
            }),
            panels: Yup.string().when('shape', ([shape], schema) => customShapeRule(shape, schema, sectionSettings.error_panels_required)),
            fold_direction: Yup.string().when('shape', ([shape], schema) => customShapeRule(shape, schema, sectionSettings.error_fold_direction_required)),
            strap_location: Yup.string().when('shape', ([shape], schema) => customShapeRule(shape, schema, sectionSettings.error_strap_location_required)),
            skirt_length: Yup.object({
                value: Yup.string(),
                variantId: metaobject.skirt_length_options.length <= 0 || metaobject.rolling_covers
                    ? Yup.string()
                    : Yup.string().required(sectionSettings.error_skirt_length_required),
                price: Yup.string()
            }),
            custom_tiedown_distance: Yup.string().when('customStrapLocation', ([customStrapLocation], schema) =>
                //@ts-ignore
                customStrapLocation ?
                    schema.required(sectionSettings.error_custom_tiedown_distance_required) :
                    schema.notRequired().nullable()
            ),
            strap_length: Yup.object({
                value: Yup.string(),
                variantId: metaobject.strap_length_options.length <= 0 || metaobject.rolling_covers
                    ? Yup.string()
                    : Yup.string().required(sectionSettings.error_strap_length_required),
                price: Yup.string()
            }),
            strap_option: Yup.object({
                value: Yup.string(),
                variantId: metaobject.strap_options.length > 0 ? Yup.string().required('required') : Yup.string(),
                price: Yup.string()
            })
        }),
        Yup.object({
            color: Yup.object({
                value: Yup.string(),
                variantId: Yup.string().required(sectionSettings.error_color_required),
                price: Yup.string()
            }),            
            handle_option: Yup.object({
                value: Yup.string(),
                variantId: metaobject.handle_options.some((item) => item.available)
                    ? Yup.string().required(sectionSettings.error_options1_required || 'required')
                    : Yup.string(),
                price: Yup.string(),
            }),
            skirt_length: Yup.object({
                value: Yup.string(),
                variantId: metaobject.skirt_length_options.length > 0 && metaobject.rolling_covers
                ? Yup.string().required(sectionSettings.error_skirt_length_required)
                : Yup.string(),
                price: Yup.string()
            }),
            strap_length: Yup.object({
                value: Yup.string(),
                variantId: metaobject.strap_length_options.length > 0 && metaobject.rolling_covers
                    ? Yup.string().required(sectionSettings.error_strap_length_required)
                : Yup.string(),
                price: Yup.string()
            }),
        }),
        Yup.object({
            shape: Yup.string().required('required'),
        })
    ]

    const [editItems, setEditItems] = useState<string[]>([]);

    const getFirstViableStep = async () => {
        const desiredStep = getCurrentStep();
        if (desiredStep < 0) return -1;
        let index = 0;
        while (index < desiredStep) {
            try {
                await stepValidations[index].validate(synthesizedInitialValues);
                index++;
            }
            catch (error) {
                console.error('validation failed for index', index);
                break;
            }
        }

        setParamByName('step', (index + 2).toString());
        return index;
    }

    const [open, setOpen] = useState(false);

    const openConfigurator = () => {
        document.body.classList.add('overflow-hidden');
        setOpen(true);
    }

    const closeConfigurator = () => {
        document.body.classList.remove('overflow-hidden');
        setOpen(false);
    }

    useEffect(() => {
        getFirstViableStep().then(result => {
            if (result >= 0) {
                openConfigurator()
            }
        }).catch(error => console.error(error))
    }, [])

    useEffect(() => {
        const variantEl = document.querySelector<HTMLInputElement>('input[name=id]');
        const querySku = getParamByName("sku");
        const openButtons = document.querySelectorAll<HTMLButtonElement>('.open-configurator-view');

        getStoredForm((returnedForm, itemKeys) => {
            if (!returnedForm) return;
            setEditItems(itemKeys);
            setInitialValues({ ...defaultInitialValues, ...returnedForm });
        });


        if(querySku) {
            const coverInput = document.querySelector<HTMLInputElement>(`input[name="configurator_variant"][data-sku="${querySku}"]`);
            if(coverInput)
                coverInput.click();
        } else {
            setCoverVariant(variantEl?.value);
        }
        const onVariant = (e: Event) => setCoverVariant((e.currentTarget as HTMLInputElement).value);
        variantEl?.addEventListener('change', onVariant);

        const onOpen = (e: Event) => {
            e.preventDefault();
            const url = (e?.currentTarget as HTMLElement).dataset.url;
            if(url == null || url == "null" || !url) {
                return openConfigurator();
            }
            
            const coverInput = document.querySelector<HTMLInputElement>(`input[name="configurator_variant"][value="${variantEl?.value}"]`);
            initializeConfiguratorJourneySource();
            window.location.href = `${url}?step=2&sku=${coverInput?.dataset.sku}&origin=${encodeURIComponent(window.location.pathname)}`
            return;
        }
        openButtons.forEach((button) => button.addEventListener('click', onOpen));

        return () => {
            variantEl?.removeEventListener('change', onVariant);
            openButtons.forEach((button) => button.removeEventListener('click', onOpen));
        };

    }, []);

    async function blobToDataURL(blob: Blob) {
        return await new Promise<string>((res, rej) => {
            const r = new FileReader();
            r.onload = () => res(String(r.result));
            r.onerror = rej;
            r.readAsDataURL(blob);
        });
    }

    async function inlineImagesFor(el: HTMLElement, selector = 'img') {
        const pairs: Array<{ node: HTMLImageElement; src: string }> = [];
        const imgs = Array.from(el.querySelectorAll(selector)) as HTMLImageElement[];

        for (const img of imgs) {
            const original = img.currentSrc || img.src;
            pairs.push({ node: img, src: original });

            try {
                // CORS-friendly fetch (will fail if the server blocks it completely)
                const resp = await fetch(original, { mode: 'cors', credentials: 'omit' });
                if (!resp.ok) continue;
                const blob = await resp.blob();
                const dataUrl = await blobToDataURL(blob);
                img.src = dataUrl;
                try { await img.decode?.(); } catch { }
            } catch {
                // If fetch fails due to CORS, you’ll need a tiny proxy endpoint (see note below)
            }
        }

        // return a restore function
        return () => pairs.forEach(p => (p.node.src = p.src));
    }

    async function captureCadWithSnapdom(): Promise<Blob> {
        const root = document.querySelector('#cad_image') as HTMLElement;
        const target = root.querySelector('.configurator-display__cad') as HTMLElement;
        if (!root || !target) throw new Error('CAD container not found');

        // let layout/paint settle
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

        // Inline only the overlay layer images to avoid touching the rest
        const restore = await inlineImagesFor(root, '.configurator-display__layer img');

        // snap at reasonable DPI
        const scale = Math.min(window.devicePixelRatio || 1, 2);
        const imgBlob = await snapdom.toBlob(root, { scale, backgroundColor: null });

        // restore DOM
        restore();
        return imgBlob; // data URL
    }

    const isCustomSize = (values: any) => {
        const currentShape = getShape(values.shape);
        let customSize = false;
        currentShape.dimensions.forEach((dim) => {
            const dimObject = values[`dimension_${dim.dimension}`];
            if (dimObject?.customSize != 'true') return;
            if (dimObject?.exemptions?.some((exemption: string) =>
                [...(values.variants ?? []),
                    values.oversize_fee,
                    values.skirt_length,
                    values.strap_length,
                    values.strap_option,
                    values.handle_option,
                    values.color].some((variant: ConfiguratorValue) => variant.variantId == exemption)))
                    return;

            customSize = true;
        })
        return customSize;
    }

    const generateItems = (values: any, errorMessage?: string) => {
        const items: any[] = [];
        const coverId = Date.now().toString();
        const journeySource = getConfiguratorJourneySource();

        let properties: any = {
            _cover_id: coverId?.toString(),
            _configurator_type: metaobject.type,
            _customShape: values.customShape?.toString() || 'false',
            _customFeatures: values.customFeatures?.toString() || 'false',
            _customSize: isCustomSize(values).toString() || 'false',
            _customStrapLocation: values.customStrapLocation?.toString() || 'false',
            _configurator_entry_path: journeySource?.entry_path || '',
            _configurator_entry_model_source: journeySource?.entry_model_source || ''
        };

        if (metaobject.rolling_covers) {
            properties._rolling_covers = 'true';
        }

        if (errorMessage) {
            properties._errorMessage = errorMessage;
        }

        Object.keys(values).forEach((key) => {
            const singleLevelObjects = ['string', 'number'];
            if (singleLevelObjects.includes(typeof values[key]) && values[key]) {
                if (key == 'fold_direction') {
                    // legacy line item property
                    properties[`_fold_location`] = values[key]?.toString();
                } else if(key == 'shape') {
                    properties[`_${key}`] = values[key]?.toString().replaceAll('_', ' ');
                } else {
                    properties[`_${key}`] = values[key]?.toString();
                }
                return;
            }

            if (typeof values[key] != 'object') return;
            if (Array.isArray(values[key])) {
                values[key].forEach((item: ConfiguratorValue) => {
                    if (item.field == "upsell_package") {
                        item.associatedVariants?.forEach((variant) =>
                            items.push({
                                id: variant,
                                quantity: 1,
                                properties: {
                                    _cover_id: coverId?.toString(),
                                    _value: item.value,
                                    _step: item.step?.toString() || '2',
                                    _cover_field: item.field,
                                    _multi_entry: 'true',
                                }
                            }));
                        return;
                    }
                    if (item.variantId) {
                        items.push({
                            id: item.variantId,
                            quantity: 1,
                            properties: {
                                _cover_id: coverId?.toString(),
                                _value: item.value,
                                _step: item.step?.toString() || '2',
                                _cover_field: item.field,
                                _multi_entry: 'true',
                            }
                        })
                    }
                });
                return;
            }
            if (!values[key]?.value) return;
            const dimensionKeys = ['dimension_a', 'dimension_b', 'dimension_c', 'dimension_d'];
            if (dimensionKeys.includes(key) && values[key].value == 0) return;
            properties[`_${key}`] = values[key].value;
            if (!values[key].variantId) return;
            const newItem = {
                id: values[key].variantId,
                quantity: 1,
                properties: {
                    _cover_id: coverId.toString(),
                    _step: values[key].step?.toString() || '2',
                    _cover_field: key,
                    _multi_entry: false,
                    _value: values[key].value,
                    _disableContinuation: values[key].disableContinuation?.toString(),
                    _price: values[key].price,
                    _associatedVariants: values[key].associatedVariants?.join(',') || ''
                }
            };
            items.push(newItem);
        })

        return { items, properties };
    }

    const addToCart = async (values: any, blob?: Blob, errorMessage?: string) => {
        const { items, properties } = generateItems(values, errorMessage);
        fetch(`${window.routes.cart_add_url}.js`, {
            body: JSON.stringify({ items: items }),
            credentials: "same-origin",
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
            }
        })
            .then((response) => response.text())
            .then((responseText) => {
                var parsedState = JSON.parse(responseText);
                if (parsedState.error) {
                    console.error(parsedState.error);
                    return;
                }
                if (parsedState.message) {
                    return;
                }
                sessionStorage.removeItem(getSessionStorageKey());
                
                clearConfiguratorJourneySource();
                submitFinalForm(blob, properties);
            })
            .catch((error) => {
                console.error(error);
            })
    }

    const submitFinalForm = (blob: Blob, properties: any) => {
        const form = document.getElementById(`product-form-${window.sectionId}`) as HTMLFormElement;
        if (blob !== null) {
            const imgFile = new File([blob], `upload_img_${Date.now()}`, { type: 'image/png' });
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(imgFile);
            const fileUploadElement = document.createElement('input');
            fileUploadElement.type = "file";
            fileUploadElement.name = "properties[_image]";
            fileUploadElement.files = dataTransfer.files;
            form.appendChild(fileUploadElement);
        }

        Object.keys(properties).forEach((key) => {
            const element = document.createElement('input');
            element.type = "hidden";
            element.name = `properties[${key}]`;
            element.value = properties[key];
            form.appendChild(element);
        });

        form.submit();
    }

    const removeFromCart = async () => {
        try {
            let updates: any = {};
            editItems.forEach((key) => {
                updates[`${key}`] = 0;
            });
            const body = JSON.stringify({ updates });
            //@ts-ignore
            const response = await fetch(`${window.routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
            const result = await response.json();
            if (result.error) {
                console.error(result.error);
                return;
            }
            return;
        }
        catch (e) {
            return;
        }
    };

    const submitConfiguratorForm = async (values: any) => {
        if (editItems.length > 0) {
            await removeFromCart();
        }
        if (values.customShape) {
            addToCart(values);
            return;
        }
        const blob = metaobject.enable_cad_step ? await captureCadWithSnapdom() : null;
        addToCart(values, blob);
    }

    return (
        <FormBuilder
            initialValues={initialValues}
            shapes={shapes}
            metaobject={metaobject}
            onSubmit={async values => submitConfiguratorForm(values)}
            open={open}
            closeConfigurator={closeConfigurator}
            getFirstViableStep={getFirstViableStep}
        >
            <FormStep
                onSubmit={() => {
                    console.log('Step1 onSubmit');
                }}
                validationSchema={stepValidations[0]}
            >
                <Step2
                    shapes={shapes}
                    metaobject={metaobject}
                />
                {metaobject.rolling_covers ? null :
                    <Step3 />
                }
            </FormStep>

            <FormStep
                onSubmit={() => console.log('onSubmit Step3')}
                validationSchema={stepValidations[1]}
            >
                <Step4 />
            </FormStep>
            <FormStep
                onSubmit={() => console.log('onSubmit Step4')}
                validationSchema={stepValidations[2]}
            >
                <Step5 />
            </FormStep>
            {metaobject.enable_cad_step && <FormStep
                onSubmit={() => console.log('onSubmit Step5')}
                validationSchema={stepValidations[3]}
            >
                <Step6 />
            </FormStep>}
        </FormBuilder>)
}

export default CoverConfigurator;